import React, { useMemo } from 'react';

// ==========================================
// 📚 MULTILINGUAL KEYBOARD MAPPINGS
// ==========================================

const KRUTI_DEV_MAP = {
  '`': ['◌', '◌्'], '1': ['1', '!'], '2': ['2', '@'], '3': ['3', '#'], '4': ['4', '$'], '5': ['5', '%'], '6': ['6', '^'], '7': ['7', '&'], '8': ['8', '*'], '9': ['9', '('], '0': ['0', ')'], '-': ['-', '_'], '=': ['त्र', 'ृ'],
  'q': ['ु', 'ू'], 'w': ['ू', '्'], 'e': ['म', 'ं'], 'r': ['त', '्र'], 't': ['ज', 'र्'], 'y': ['ल', 'ी'], 'u': ['न', 'ि'], 'i': ['प', '◌'], 'o': ['व', '◌'], 'p': ['च', 'ा'], '[': ['ख', 'क्ष'], ']': [',', 'द्व'], '\\': ['?', '?'],
  'a': ['ं', 'ो'], 's': ['े', '्'], 'd': ['क', 'क्'], 'f': ['ि', 'थ'], 'g': ['ह', 'ळ'], 'h': ['ी', 'भ'], 'j': ['र', 'श्र'], 'k': ['ा', 'ज्ञ'], 'l': ['स', 'स्'], ';': ['य', 'रू'], "'": ['श', 'ष्'],
  'z': ['्र', 'र्'], 'x': ['ग', 'ग्'], 'c': ['ब', 'ब्'], 'v': ['अ', 'ट'], 'b': ['इ', 'ठ'], 'n': ['द', 'छ'], 'm': ['उ', 'ड'], ',': ['ए', 'ढ'], '.': ['ण्', 'झ'], '/': ['ध', 'घ']
};

const MANGAL_INSCRIPT_MAP = {
    '`': ['◌', '◌'], '1': ['१', 'ऍ'], '2': ['२', 'र्◌'], '3': ['३', 'रु'], '4': ['४', '₹'], '5': ['५', 'ज्ञ'], '6': ['६', 'त्र'], '7': ['७', 'क्ष'], '8': ['८', 'श्र'], '9': ['९', '('], '0': ['०', ')'], '-': ['-', 'ः'], '=': ['=', 'ृ'],
    'q': ['ौ', 'औ'], 'w': ['ै', 'ऐ'], 'e': ['ा', 'आ'], 'r': ['ी', 'ई'], 't': ['ू', 'ऊ'], 'y': ['ब', 'भ'], 'u': ['ह', 'ङ'], 'i': ['ग', 'घ'], 'o': ['द', 'ध'], 'p': ['ज', 'झ'], '[': ['ड', 'ढ'], ']': ['़', 'ञ'], '\\': ['ॉ', 'ऑ'],
    'a': ['ो', 'ओ'], 's': ['े', 'ए'], 'd': ['्', 'अ'], 'f': ['ि', 'इ'], 'g': ['ु', 'उ'], 'h': ['प', 'फ'], 'j': ['र', 'ऱ'], 'k': ['क', 'ख'], 'l': ['त', 'थ'], ';': ['च', 'छ'], "'": ['ट', 'ठ'],
    'z': ['े', 'ऎ'], 'x': ['ं', 'ँ'], 'c': ['म', 'ण'], 'v': ['न', 'ऩ'], 'b': ['व', 'ळ'], 'n': ['ल', 'ऴ'], 'm': ['स', 'श'], ',': [',', 'ष'], '.': ['.', '।'], '/': ['य', 'य़']
};

const REMINGTON_GAIL_MAP = {
    '`': ['◌', '◌'], '1': ['1', '!'], '2': ['2', '@'], '3': ['3', '#'], '4': ['4', '$'], '5': ['5', '%'], '6': ['6', '^'], '7': ['7', '&'], '8': ['8', '*'], '9': ['9', '('], '0': ['0', ')'], '-': ['-', '_'], '=': ['ृ', 'त्र'],
    'q': ['ु', 'फ'], 'w': ['ू', 'ॅ'], 'e': ['म', 'म्'], 'r': ['त', 'त्'], 't': ['ज', 'ज्'], 'y': ['ल', 'ल्'], 'u': ['न', 'न्'], 'i': ['प', 'प्'], 'o': ['व', 'व्'], 'p': ['च', 'च्'], '[': ['ख', 'क्ष'], ']': [',', 'द्व'], '\\': ['?', '?'],
    'a': ['ं', 'ँ'], 's': ['े', 'ै'], 'd': ['क', 'क्'], 'f': ['ि', 'थ'], 'g': ['ह', 'ळ'], 'h': ['ी', 'भ'], 'j': ['र', 'श्र'], 'k': ['ा', 'ज्ञ'], 'l': ['स', 'स्'], ';': ['य', 'रू'], "'": ['श', 'ष्'],
    'z': ['्र', 'र्'], 'x': ['ग', 'ग्'], 'c': ['ब', 'ब्'], 'v': ['अ', 'ट'], 'b': ['इ', 'ठ'], 'n': ['द', 'छ'], 'm': ['उ', 'ड'], ',': ['ए', 'ढ'], '.': ['ण्', 'झ'], '/': ['ध', 'घ']
};

export default function VirtualKeyboard({ isDarkMode, typingLanguage, needsShift, activeKey, targetKeyToHighlight }) {
  
  const tStyle = {
    bg: 'transparent',
    keyBg: '#1e293b', 
    keyText: '#f8fafc', 
    keyBorder: '#0f172a',
    specialBg: '#334155',
    activeBg: '#8b5cf6', 
    targetBg: '#eab308',   
    hintText: '#64748b'
  };

  const currentMap = useMemo(() => {
    switch (typingLanguage) {
      case 'kruti_dev': return KRUTI_DEV_MAP;
      case 'inscript': return MANGAL_INSCRIPT_MAP;
      case 'remington_gail': 
      case 'remington_cbi': return REMINGTON_GAIL_MAP;
      default: return null; 
    }
  }, [typingLanguage]);

  const rows = [
    ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=', 'Backspace'],
    ['Tab', 'q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', '[', ']', '\\'],
    ['CapsLock', 'a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', "'", 'Enter'],
    ['ShiftLeft', 'z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/', 'ShiftRight'],
    ['Control', 'Alt', 'Space', 'Alt', 'Control']
  ];

  // 🚀 SHRUNK WIDTHS to provide margin on the sides
  const getKeyWidth = (keyId) => {
    switch (keyId) {
      case 'Backspace': return '75px';
      case 'Tab': return '55px';
      case 'CapsLock': return '68px';
      case 'Enter': return '75px';
      case 'ShiftLeft': 
      case 'ShiftRight': return '90px';
      case 'Space': return '250px';
      case 'Control': 
      case 'Alt': return '50px';
      default: return '38px'; // Shrunk from 42px
    }
  };

  const getCharacters = (keyId) => {
    const isSpecial = ['Backspace', 'Tab', 'CapsLock', 'Enter', 'ShiftLeft', 'ShiftRight', 'Control', 'Alt', 'Space'].includes(keyId);
    if (isSpecial) return { special: keyId.replace('Left', '').replace('Right', '') };

    if (!currentMap) {
      const symbols = { '`': '~', '1': '!', '2': '@', '3': '#', '4': '$', '5': '%', '6': '^', '7': '&', '8': '*', '9': '(', '0': ')', '-': '_', '=': '+', '[': '{', ']': '}', '\\': '|', ';': ':', "'": '"', ',': '<', '.': '>', '/': '?' };
      if (symbols[keyId]) return { hint: symbols[keyId], main: keyId };
      return { hint: keyId.toUpperCase(), main: keyId.toLowerCase() };
    }

    const mapEntry = currentMap[keyId] || [keyId, keyId.toUpperCase()];
    return { hint: keyId.toUpperCase(), shiftHindi: mapEntry[1], main: mapEntry[0] };
  };

  return (
    <div style={{ padding: '5px', backgroundColor: tStyle.bg, fontFamily: 'sans-serif', userSelect: 'none', display: 'flex', flexDirection: 'column', gap: '5px', width: 'max-content', margin: '0 auto' }}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
          {row.map((keyId, idx) => {
            const chars = getCharacters(keyId);
            
            let isPressed = false;
            if (activeKey) {
                const lowerActive = activeKey.toLowerCase();
                if (lowerActive === keyId.toLowerCase()) isPressed = true;
                if ((keyId === 'ShiftLeft' || keyId === 'ShiftRight') && activeKey === 'Shift') isPressed = true;
                if (keyId === 'Space' && activeKey === ' ') isPressed = true;
            }

            let isTarget = false;
            if (targetKeyToHighlight) {
                const lowerTarget = targetKeyToHighlight.toLowerCase();
                
                if (lowerTarget === keyId.toLowerCase()) isTarget = true;
                if ((keyId === 'ShiftLeft' || keyId === 'ShiftRight') && (targetKeyToHighlight === 'shift' || needsShift)) isTarget = true;
                if (keyId === 'Space' && targetKeyToHighlight === 'space') isTarget = true;
                if (keyId === 'Enter' && targetKeyToHighlight === 'enter') isTarget = true;
            }

            const isSpecial = !!chars.special;
            
            let bg = isSpecial ? tStyle.specialBg : tStyle.keyBg;
            let textColor = tStyle.keyText;
            
            if (isTarget) {
              bg = tStyle.targetBg; 
              textColor = '#000000'; 
            } else if (isPressed) {
              bg = tStyle.activeBg; 
              textColor = 'white';
            }

            const fontFam = typingLanguage === 'kruti_dev' ? "'Kruti Dev 010', sans-serif" : "sans-serif";

            return (
              <div 
                key={`${rowIndex}-${idx}`} 
                style={{ 
                  width: getKeyWidth(keyId), 
                  height: '42px', // 🚀 Shrunk height from 46px
                  backgroundColor: bg, 
                  color: textColor, 
                  border: `1px solid ${tStyle.keyBorder}`, 
                  borderRadius: '6px', 
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isPressed ? 'inset 0 3px 5px rgba(0,0,0,0.3)' : `0 3px 0 ${tStyle.keyBorder}`,
                  transform: isPressed ? 'translateY(3px)' : 'none',
                  transition: 'all 0.1s'
                }}
              >
                {chars.special ? (
                  <span style={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }}>{chars.special}</span>
                ) : (
                  <>
                    <span style={{ position: 'absolute', top: '3px', left: '5px', fontSize: '9px', color: isTarget ? '#000' : tStyle.hintText, fontWeight: 'bold', fontFamily: 'sans-serif' }}>
                      {chars.hint}
                    </span>

                    {chars.shiftHindi && (
                      <span style={{ position: 'absolute', top: '3px', right: '5px', fontSize: '11px', color: needsShift ? (isTarget ? '#000' : '#fff') : (isTarget ? 'rgba(0,0,0,0.5)' : tStyle.hintText), fontFamily: fontFam }}>
                        {chars.shiftHindi}
                      </span>
                    )}

                    <span style={{ position: 'absolute', bottom: '4px', fontSize: '16px', fontWeight: 'bold', fontFamily: fontFam, opacity: needsShift && chars.shiftHindi ? 0.4 : 1 }}>
                      {chars.main}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}